import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../../styled-system/css";
import { Grid } from "../../../../../styled-system/jsx";
import { ExplainerBox } from "../../../../components/core/basic/ExplainerBox";
import { useAppSelector } from "../../../../redux/hooks";
import { formatNumber, formatCurrency } from "../../../../utils/formatNumber";
import { average } from "../../../../utils/math";
import { useFetch } from "../../../../hooks/api";
import moment from "moment";
import { keepPreviousData } from "@tanstack/react-query";

interface Props {
  strategySlider: number;
  selectedProducts: number[];
  className?: string;
}

export function MonthlyEstimates({ strategySlider, selectedProducts, className }: Props) {
  const { t } = useTranslation(["pricingv2"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const end = moment().subtract(1, "days");
  const start = end.subtract(30, "days");
  const preEnd = start.clone().subtract(1, "days");
  const preStart = preEnd.subtract(30, "days");

  const filters = {
    mp: marketPlace,
    date_group: "day",
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    group_by: "product_id",
  };

  const productReportQuery = useFetch<{ date: { total_units: number }[]; products: { product_price: number }[] }>(
    ["product", marketPlace, selectedProducts],
    `/api/data_report/product_report?filters=product_ids:${selectedProducts.join(",")}`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const expectedData = useMemo(() => {
    const expectedData = {
      floor_units: 0,
      ceiling_units: 0,
      floor_sales: 0,
      ceiling_sales: 0,
      floor_price: 0,
      ceiling_price: 0,
    };

    if (!productReportQuery.isSuccess) {
      return expectedData;
    }

    const prices: number[] = [];
    const unitsHistory = [];

    for (const stat of productReportQuery.data.date) {
      unitsHistory.push(stat.total_units);
    }

    for (const stat of productReportQuery.data.products) {
      prices.push(stat.product_price);
    }

    const thirtyDayUnits = average(unitsHistory);
    let unitsMod = thirtyDayUnits;
    const price = average(prices);
    let priceMod = price;

    if (strategySlider === -2) {
      priceMod = price - price * 0.05;
      unitsMod = thirtyDayUnits * 1.2;
      expectedData.floor_units = thirtyDayUnits;
      expectedData.ceiling_units = unitsMod;
    } else if (strategySlider === -1) {
      unitsMod = thirtyDayUnits * 1.1;
      priceMod = price - price * 0.03;
      expectedData.floor_units = thirtyDayUnits;
      expectedData.ceiling_units = unitsMod;
    } else if (strategySlider === 1) {
      unitsMod = thirtyDayUnits * 0.9;
      priceMod = price + price * 0.03;
      expectedData.floor_units = unitsMod;
      expectedData.ceiling_units = thirtyDayUnits;
    } else if (strategySlider === 2) {
      unitsMod = thirtyDayUnits * 0.8;
      priceMod = price + price * 0.05;
      expectedData.floor_units = unitsMod;
      expectedData.ceiling_units = thirtyDayUnits;
    } else {
      priceMod = price + price * 0.01;
      expectedData.floor_units = thirtyDayUnits;
      expectedData.ceiling_units = thirtyDayUnits;
    }

    expectedData.floor_sales = expectedData.floor_units * Math.min(priceMod, price);
    expectedData.floor_price = Math.min(priceMod, price);
    expectedData.ceiling_sales = expectedData.ceiling_units * Math.max(priceMod, price);
    expectedData.ceiling_price = Math.max(priceMod, price);

    return expectedData;
  }, [productReportQuery.isSuccess, productReportQuery.data, strategySlider]);

  return (
    <ExplainerBox className={className} fontSize={"medium"}>
      <div className={css({ fontWeight: 700 })}>{t(($) => $.pricingv2["Monthly Estimates"])}</div>
      {productReportQuery.isLoading && <div>{t(($) => $.pricingv2["Estimates are loading..."])}</div>}
      {productReportQuery.isError && <div>{t(($) => $.pricingv2["Failed to load estimates."])}</div>}
      {productReportQuery.isSuccess && productReportQuery.data.products.length === 0 && (
        <div>{t(($) => $.pricingv2["No data available for selected products."])}</div>
      )}
      {productReportQuery.isSuccess && productReportQuery.data.products.length > 0 && (
        <>
          <Grid gap={"xsmall"} columns={2}>
            <span>{t(($) => $.pricingv2["Volume:"])}</span>
            <span>
              {formatNumber(expectedData.floor_units)}
              <span>{strategySlider !== 0 ? ` - ${formatNumber(expectedData.ceiling_units)}` : ""}</span>
            </span>
            <span>{t(($) => $.pricingv2["Price:"])}</span>
            <span>
              {`${formatCurrency(expectedData.floor_price, {
                marketPlace: marketPlace,
              })} - ${formatCurrency(expectedData.ceiling_price, {
                marketPlace: marketPlace,
              })}`}
            </span>
            <span>{t(($) => $.pricingv2["Sales:"])}</span>
            <span>
              {`${formatCurrency(expectedData.floor_sales, {
                marketPlace: marketPlace,
              })} - ${formatCurrency(expectedData.ceiling_sales, {
                marketPlace: marketPlace,
              })}`}
            </span>
          </Grid>
          <small>
            <em>{t(($) => $.pricingv2["** Based on historical 30 day sales data."])}</em>
          </small>
        </>
      )}
    </ExplainerBox>
  );
}
